/**
 * PatternBrowser — modal CRUD UI for sequencer patterns.
 *
 * Mirrors the top-level PresetBrowser (save row + grouped list +
 * search) but trimmed for sequencer state and rendered as a centered
 * overlay instead of a slide-in panel. Patterns persist via the
 * caller-supplied PatternStore; this component is purely UI.
 *
 * Layout:
 *
 *   ┌─ overlay backdrop ────────────────────────────────────────┐
 *   │   ┌─ modal ────────────────────────────────────────────┐   │
 *   │   │ Patterns                                       ✕  │   │
 *   │   │ [search……]                                         │   │
 *   │   │ [name….]  [category ▼] [+ new cat]  [Save]         │   │
 *   │   │ ── Drums ───                                       │   │
 *   │   │   Hi-hat shuffle    [load]  [✎]  [✕]                │   │
 *   │   │   Kick four          [load]  [✎]  [✕]                │   │
 *   │   │ ── Bass ────                                       │   │
 *   │   │   Acid roll          [load]  [✎]  [✕]                │   │
 *   │   └─────────────────────────────────────────────────────┘   │
 *   └────────────────────────────────────────────────────────────┘
 *
 * The host owns when to open / close the modal — `open()` /
 * `close()` are the public hooks. Click outside the modal or hit
 * Escape to close.
 */

import {
  type PatternData,
  type PatternStore,
  DEFAULT_PATTERN_CATEGORY,
} from '@ac-slayer/synth-core';
import type { Sequencer, SequencerStep } from '@ac-slayer/synth-core';

export interface PatternBrowserOptions {
  readonly store: PatternStore;
  readonly sequencer: Sequencer;
  /**
   * Called when a pattern is loaded so the host can stash the active
   * id (e.g. for "save over" UX). Optional — when omitted the panel
   * tracks the active id locally.
   */
  readonly onLoaded?: (id: string) => void;
}

export class PatternBrowser {
  /** Click anywhere on this element to dismiss. The actual modal
   *  card sits inside as a child. */
  readonly element: HTMLElement;

  private readonly store: PatternStore;
  private readonly sequencer: Sequencer;
  private readonly onLoaded: (id: string) => void;

  private readonly searchInput: HTMLInputElement;
  private readonly nameInput: HTMLInputElement;
  private readonly categorySelect: HTMLSelectElement;
  private readonly newCategoryInput: HTMLInputElement;
  private readonly listEl: HTMLElement;
  private readonly emptyEl: HTMLElement;

  private activePatternId: string | null = null;
  private searchQuery = '';
  private isOpen = false;
  private readonly unsubscribeStore: () => void;
  private readonly onKeyDown = (e: KeyboardEvent): void => {
    if (e.key === 'Escape' && this.isOpen) {
      e.preventDefault();
      this.close();
    }
  };

  constructor(opts: PatternBrowserOptions) {
    this.store = opts.store;
    this.sequencer = opts.sequencer;
    this.onLoaded = opts.onLoaded ?? ((): void => {});

    // ── Backdrop (full-viewport overlay, click to dismiss) ───────
    this.element = document.createElement('div');
    this.element.className = 'pattern-modal-backdrop';
    this.element.hidden = true;
    this.element.addEventListener('mousedown', (e) => {
      if (e.target === this.element) this.close();
    });

    // ── Modal card ───────────────────────────────────────────────
    const card = document.createElement('div');
    card.className = 'pattern-modal';
    card.setAttribute('role', 'dialog');
    card.setAttribute('aria-label', 'Pattern browser');
    this.element.appendChild(card);

    const header = document.createElement('header');
    header.className = 'pattern-modal-header';
    const title = document.createElement('h2');
    title.className = 'pattern-modal-title';
    title.textContent = 'Patterns';
    header.appendChild(title);
    const closeBtn = document.createElement('button');
    closeBtn.type = 'button';
    closeBtn.className = 'pattern-modal-close';
    closeBtn.textContent = '✕';
    closeBtn.title = 'Close';
    closeBtn.addEventListener('click', () => this.close());
    header.appendChild(closeBtn);
    card.appendChild(header);

    // ── Search ───────────────────────────────────────────────────
    const searchRow = document.createElement('div');
    searchRow.className = 'pattern-modal-search';
    this.searchInput = document.createElement('input');
    this.searchInput.type = 'search';
    this.searchInput.className = 'pattern-modal-input mono';
    this.searchInput.placeholder = 'search patterns…';
    this.searchInput.setAttribute('aria-label', 'Search patterns');
    this.searchInput.addEventListener('input', () => {
      this.searchQuery = this.searchInput.value.trim().toLowerCase();
      this.render();
    });
    searchRow.appendChild(this.searchInput);
    card.appendChild(searchRow);

    // ── Save row ─────────────────────────────────────────────────
    const form = document.createElement('div');
    form.className = 'pattern-modal-form';

    this.nameInput = document.createElement('input');
    this.nameInput.type = 'text';
    this.nameInput.className = 'pattern-modal-input mono';
    this.nameInput.placeholder = 'name this pattern…';
    this.nameInput.setAttribute('aria-label', 'Pattern name');
    this.nameInput.addEventListener('keydown', (e) => {
      if (e.key === 'Enter') this.handleSave();
    });

    this.categorySelect = document.createElement('select');
    this.categorySelect.className = 'pattern-modal-input mono';
    this.categorySelect.setAttribute('aria-label', 'Category');

    this.newCategoryInput = document.createElement('input');
    this.newCategoryInput.type = 'text';
    this.newCategoryInput.className = 'pattern-modal-input mono';
    this.newCategoryInput.placeholder = 'new category';
    this.newCategoryInput.hidden = true;
    this.newCategoryInput.setAttribute('aria-label', 'New category name');
    this.newCategoryInput.addEventListener('keydown', (e) => {
      if (e.key === 'Enter') this.handleSave();
    });

    this.categorySelect.addEventListener('change', () => {
      const isNew = this.categorySelect.value === '__new__';
      this.newCategoryInput.hidden = !isNew;
      if (isNew) this.newCategoryInput.focus();
    });

    const saveBtn = document.createElement('button');
    saveBtn.type = 'button';
    saveBtn.className = 'btn btn-sm';
    saveBtn.textContent = '+ Save';
    saveBtn.addEventListener('click', () => this.handleSave());

    form.append(this.nameInput, this.categorySelect, this.newCategoryInput, saveBtn);
    card.appendChild(form);

    // ── List + empty state ───────────────────────────────────────
    this.listEl = document.createElement('div');
    this.listEl.className = 'pattern-modal-list';
    card.appendChild(this.listEl);

    this.emptyEl = document.createElement('div');
    this.emptyEl.className = 'pattern-modal-empty label';
    this.emptyEl.textContent = 'No saved patterns yet.';
    card.appendChild(this.emptyEl);

    this.unsubscribeStore = this.store.subscribe(() => {
      this.refreshCategoryOptions();
      this.render();
    });

    this.refreshCategoryOptions();
    this.render();
  }

  /** Show the modal. Re-focuses search so the user can type-to-find
   *  immediately. */
  open(): void {
    this.isOpen = true;
    this.element.hidden = false;
    document.addEventListener('keydown', this.onKeyDown);
    requestAnimationFrame(() => {
      if (this.isOpen) this.searchInput.focus();
    });
  }

  close(): void {
    this.isOpen = false;
    this.element.hidden = true;
    document.removeEventListener('keydown', this.onKeyDown);
  }

  /** Tell the browser which pattern is currently loaded — drives the
   *  "active" highlight + same-name overwrite UX. */
  setActiveId(id: string | null): void {
    this.activePatternId = id;
    this.render();
  }

  dispose(): void {
    this.close();
    this.unsubscribeStore();
  }

  // ── Internals ─────────────────────────────────────────────────────────

  private matchesSearch(p: PatternData): boolean {
    if (this.searchQuery === '') return true;
    const q = this.searchQuery;
    return (
      p.name.toLowerCase().includes(q) ||
      p.category.toLowerCase().includes(q)
    );
  }

  private currentCategory(): string {
    if (this.categorySelect.value === '__new__') {
      const typed = this.newCategoryInput.value.trim();
      return typed || DEFAULT_PATTERN_CATEGORY;
    }
    return this.categorySelect.value || DEFAULT_PATTERN_CATEGORY;
  }

  private snapshotSequencerState(): {
    rate: string;
    length: number;
    swing: number;
    steps: SequencerStep[];
  } {
    return {
      rate: String(this.sequencer.rate.value),
      length: this.sequencer.length.value,
      swing: this.sequencer.swing.value,
      steps: this.sequencer.steps.map((s) => ({ ...s })),
    };
  }

  private handleSave(): void {
    const name = this.nameInput.value.trim();
    if (!name) {
      this.nameInput.focus();
      return;
    }
    const category = this.currentCategory();
    const snap = this.snapshotSequencerState();
    // Same-name overwrite on the loaded pattern keeps iterative
    // saves from filling the list with copies.
    if (this.activePatternId !== null) {
      const existing = this.store.get(this.activePatternId);
      if (existing && existing.name === name) {
        this.store.update(existing.id, { ...snap, category });
        return;
      }
    }
    const id = this.store.put({ name, category, ...snap });
    this.activePatternId = id;
    this.onLoaded(id);
    this.nameInput.value = '';
  }

  private handleLoad(p: PatternData): void {
    this.sequencer.rate.set(p.rate as never, 'preset');
    this.sequencer.length.set(p.length, 'preset');
    this.sequencer.swing.set(p.swing, 'preset');
    this.sequencer.setSteps(p.steps);
    this.activePatternId = p.id;
    this.onLoaded(p.id);
    this.render();
    this.close();
  }

  private handleRename(p: PatternData): void {
    const newName = window.prompt('Rename pattern', p.name);
    if (newName === null) return;
    const trimmed = newName.trim();
    if (!trimmed) return;
    // Reuse the category select's current value if it differs from
    // the pattern's existing category, so a single dialog can
    // recategorize while renaming. If select === pattern.category,
    // leave it.
    const pickedCategory = this.currentCategory();
    const nextCategory = pickedCategory !== p.category ? pickedCategory : p.category;
    this.store.update(p.id, { name: trimmed, category: nextCategory });
  }

  private handleDelete(p: PatternData): void {
    if (!window.confirm(`Delete pattern "${p.name}"?`)) return;
    this.store.delete(p.id);
    if (this.activePatternId === p.id) {
      this.activePatternId = null;
    }
  }

  private refreshCategoryOptions(): void {
    const previous = this.categorySelect.value;
    this.categorySelect.innerHTML = '';
    for (const cat of this.store.categories()) {
      const o = document.createElement('option');
      o.value = cat;
      o.textContent = cat;
      this.categorySelect.appendChild(o);
    }
    const newOpt = document.createElement('option');
    newOpt.value = '__new__';
    newOpt.textContent = '— new category —';
    this.categorySelect.appendChild(newOpt);
    // Restore previous selection when still valid; else default.
    if (
      previous &&
      Array.from(this.categorySelect.options).some((o) => o.value === previous)
    ) {
      this.categorySelect.value = previous;
    } else {
      this.categorySelect.value = DEFAULT_PATTERN_CATEGORY;
    }
    this.newCategoryInput.hidden = this.categorySelect.value !== '__new__';
  }

  private render(): void {
    const all = this.store.list();
    const filtered = all.filter((p) => this.matchesSearch(p));
    this.listEl.innerHTML = '';
    if (all.length === 0) {
      this.emptyEl.hidden = false;
      this.emptyEl.textContent = 'No saved patterns yet.';
      return;
    }
    if (filtered.length === 0) {
      this.emptyEl.hidden = false;
      this.emptyEl.textContent = `No patterns match "${this.searchQuery}".`;
      return;
    }
    this.emptyEl.hidden = true;
    // Group by category, with starter ordering: known starters first,
    // then alphabetical, then DEFAULT_PATTERN_CATEGORY at the bottom.
    const groups = new Map<string, PatternData[]>();
    for (const p of filtered) {
      const cat = p.category || DEFAULT_PATTERN_CATEGORY;
      const list = groups.get(cat) ?? [];
      list.push(p);
      groups.set(cat, list);
    }
    const orderedNames = [...groups.keys()].sort((a, b) => {
      if (a === DEFAULT_PATTERN_CATEGORY) return 1;
      if (b === DEFAULT_PATTERN_CATEGORY) return -1;
      return a.localeCompare(b);
    });
    for (const cat of orderedNames) {
      const heading = document.createElement('div');
      heading.className = 'pattern-modal-heading label';
      heading.textContent = cat;
      this.listEl.appendChild(heading);
      for (const p of groups.get(cat)!) {
        this.listEl.appendChild(this.renderRow(p));
      }
    }
  }

  private renderRow(p: PatternData): HTMLElement {
    const row = document.createElement('div');
    row.className = 'pattern-modal-row';
    if (p.id === this.activePatternId) row.classList.add('active');

    const loadBtn = document.createElement('button');
    loadBtn.type = 'button';
    loadBtn.className = 'pattern-modal-load';
    loadBtn.title = `Load ${p.name}`;
    const nameEl = document.createElement('span');
    nameEl.className = 'pattern-modal-name';
    nameEl.textContent = p.name;
    loadBtn.appendChild(nameEl);
    loadBtn.addEventListener('click', () => this.handleLoad(p));
    row.appendChild(loadBtn);

    const renameBtn = document.createElement('button');
    renameBtn.type = 'button';
    renameBtn.className = 'pattern-modal-action';
    renameBtn.textContent = '✎';
    renameBtn.title = 'Rename / recategorize';
    renameBtn.addEventListener('click', (e) => {
      e.stopPropagation();
      this.handleRename(p);
    });
    row.appendChild(renameBtn);

    const deleteBtn = document.createElement('button');
    deleteBtn.type = 'button';
    deleteBtn.className = 'pattern-modal-action pattern-modal-delete';
    deleteBtn.textContent = '✕';
    deleteBtn.title = 'Delete';
    deleteBtn.addEventListener('click', (e) => {
      e.stopPropagation();
      this.handleDelete(p);
    });
    row.appendChild(deleteBtn);

    return row;
  }
}
